/**
 * PercentileTickInline — compact sparkline showing where this school ranks.
 * Matches detail-card.jsx PercentileTickInline.
 *
 * Shows 8 (mobile) or 10 (default) cells; the active cell is taller.
 */
import React from 'react';

interface Props {
  percentile: number;
  tier: 'high' | 'medium' | 'low' | null;
  mobile?: boolean;
}

/** Tier accent hex colors — mirrors detail-atoms.jsx tierAccent(). */
function tierAccentHex(tier: Props['tier']): string {
  switch (tier) {
    case 'high':   return '#1B5E42';
    case 'medium': return '#A0671E';
    case 'low':    return '#9C3838';
    default:       return '#C7B7A0';
  }
}

export function PercentileTickInline({ percentile, tier, mobile = false }: Props) {
  const cells = mobile ? 8 : 10;
  const accent = tierAccentHex(tier);
  const activeIdx = Math.max(0, Math.min(cells - 1, Math.floor(percentile / (100 / cells))));

  return (
    <span
      className="inline-flex items-end gap-[1.5px]"
      title={`${percentile}th percentile`}
      aria-label={`${percentile}th percentile`}
    >
      {Array.from({ length: cells }).map((_, i) => {
        const isActive = i === activeIdx;
        const past = i < activeIdx;
        return (
          <span
            key={i}
            aria-hidden="true"
            style={{
              width: 4,
              height: isActive ? 9 : past ? 5 : 4,
              background: isActive ? accent : past ? '#C7B7A0' : '#E9E6E1',
              borderRadius: 1,
              display: 'inline-block',
            }}
          />
        );
      })}
    </span>
  );
}
